import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store';
import { getFeedbackCategories, getFeedbackTopics } from '../services/feedbackApi';
import type { FeedbackCategory, FeedbackTopic, FeedbackListResponse } from '../types';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import FeedbackList from '../components/feedback/FeedbackList';
import FeedbackDetailModal from '../components/feedback/FeedbackDetailModal';
import CreateFeedbackModal from '../components/feedback/CreateFeedbackModal';
import AuthModal from '../components/auth/AuthModal';
import ProfileModal from '../components/auth/ProfileModal';
import '../styles/FeedbackPage.css';

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, setActiveTab } = useAppStore();

  // 认证模态框状态
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // 状态管理
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [topics, setTopics] = useState<FeedbackTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 筛选和分页状态
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // 详情模态框状态
  const [selectedTopic, setSelectedTopic] = useState<FeedbackTopic | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // 创建反馈模态框
  const [showCreateModal, setShowCreateModal] = useState(false);

  // 设置当前活动标签
  useEffect(() => {
    setActiveTab('feedback');
  }, [setActiveTab]);

  // 加载分类数据
  useEffect(() => {
    loadCategories();
  }, []);

  // 加载反馈列表
  useEffect(() => {
    loadTopics();
  }, [selectedCategory, selectedStatus, searchQuery, sortBy, currentPage]);

  const loadCategories = async () => {
    try {
      const data = await getFeedbackCategories();
      setCategories(data);
    } catch (err) {
      console.error('加载分类失败:', err);
      setError('加载分类失败');
    }
  };

  const loadTopics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: currentPage,
        per_page: 20,
        ...(selectedCategory && { category_id: selectedCategory }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(searchQuery && { search: searchQuery }),
        sort_by: sortBy
      };
      
      const data: FeedbackListResponse = await getFeedbackTopics(params);
      setTopics(data.topics);
      setTotalPages(data.pagination.pages);
      setTotalCount(data.pagination.total);
    } catch (err) {
      console.error('加载反馈列表失败:', err);
      setError('加载反馈列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (topic: FeedbackTopic) => {
    setSelectedTopic(topic);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTopic(null);
    // 重新加载列表以更新浏览次数等
    loadTopics();
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setCurrentPage(1);
    loadTopics();
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (categoryId: number | null) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 处理侧边栏导航
  const handleTabChange = (tab: 'search' | 'favorites' | 'trending' | 'feedback' | 'payment') => {
    if (tab === 'feedback') {
      // 已经在反馈页面，不需要导航
      return;
    }

    if (tab === 'payment') {
      navigate('/payment');
    } else {
      // 导航到主页并切换标签
      setActiveTab(tab);
      navigate('/');
    }
  };

  // 处理认证模态框
  const handleShowLogin = () => {
    setAuthMode('login');
    setShowAuthModal(true);
  };

  const handleShowProfile = () => {
    setShowProfileModal(true);
  };

  return (
    <div className="app-layout">
      {/* 左侧边栏 */}
      <Sidebar
        activeTab="feedback"
        onTabChange={handleTabChange}
      />

      {/* 主内容区 */}
      <div className="main-content">
        {/* 头部 */}
        <Header
          onShowLogin={handleShowLogin}
          onShowProfile={handleShowProfile}
        />

        {/* 内容区域 */}
        <div className="content-area">
          <div className="feedback-page">
            {/* 页面头部 */}
            <div className="feedback-header">
              <div className="feedback-header-content">
                <div className="feedback-title-section">
                  <h1 className="feedback-title">问题反馈</h1>
                  <p className="feedback-subtitle">欢迎提出您的建议和问题，帮助我们改进产品</p>
                </div>
                {isAuthenticated && (
                  <button
                    className="btn btn-primary feedback-create-btn"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <i className="fas fa-plus me-2"></i>
                    发布反馈
                  </button>
                )}
              </div>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="feedback-error">
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              </div>
            )}

            {/* 反馈列表 */}
            <div className="feedback-content">
              <FeedbackList
                categories={categories}
                topics={topics}
                loading={loading}
                selectedCategory={selectedCategory}
                selectedStatus={selectedStatus}
                searchQuery={searchQuery}
                sortBy={sortBy}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onTopicClick={handleTopicClick}
                onSearch={handleSearch}
                onCategoryFilter={handleCategoryFilter}
                onStatusFilter={handleStatusFilter}
                onSortChange={handleSortChange}
                onPageChange={handlePageChange}
              />
            </div>

            {/* 创建反馈模态框 */}
            {showCreateModal && (
              <CreateFeedbackModal
                categories={categories}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleCreateSuccess}
              />
            )}

            {/* 未登录提示 */}
            {!isAuthenticated && (
              <div className="feedback-login-prompt">
                <div className="alert alert-info text-center" role="alert">
                  <i className="fas fa-info-circle me-2"></i>
                  请先登录后再发布反馈和参与讨论
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 认证模态框 */}
      <AuthModal
        show={showAuthModal}
        mode={authMode}
        onHide={() => setShowAuthModal(false)}
        onSwitchMode={(mode) => setAuthMode(mode)}
      />

      <ProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
      />

      {/* 反馈详情模态框 */}
      <FeedbackDetailModal
        show={showDetailModal}
        topic={selectedTopic}
        onHide={handleCloseDetailModal}
        onTopicUpdate={loadTopics}
      />
    </div>
  );
};

export default FeedbackPage;

